import { createContext, useContext, useState, useCallback } from 'react';

const translations = {
  en: {
    // Navigation
    nav_home: "Home",
    nav_services: "Services",
    nav_fleet: "Fleet",
    nav_booking: "Book Now",
    nav_contact: "Contact",
    nav_admin: "Admin",
    
    // Hero
    hero_title: "Exclusive Tour Transfer",
    hero_subtitle: "Premium Chauffeur Service in Switzerland",
    hero_description: "Experience luxury travel with our professional chauffeur service. From airport transfers to city tours, we deliver excellence.",
    hero_cta: "Book Your Transfer",
    hero_calculate: "Calculate Price",
    
    // Services
    services_title: "Our Services",
    services_subtitle: "Premium transfer solutions tailored to your needs",
    service_airport: "Airport Transfers",
    service_airport_desc: "Comfortable and punctual airport pickup and drop-off services with flight monitoring.",
    service_distance: "Long Distance",
    service_distance_desc: "Travel between cities in Switzerland and Germany with our premium vehicles.",
    service_hourly: "Hourly Booking",
    service_hourly_desc: "Flexible hourly chauffeur service for business meetings or sightseeing tours.",
    
    // Fleet
    fleet_title: "Our Fleet",
    fleet_subtitle: "Premium vehicles for every occasion",
    fleet_limousine: "Premium Limousine",
    fleet_limousine_desc: "Up to 3 passengers",
    fleet_vclass: "Business Class",
    fleet_vclass_desc: "5-7 passengers",
    fleet_sprinter: "Sprinter",
    fleet_sprinter_desc: "Up to 8 passengers",
    fleet_sprinter_note: "Check Availability",
    fleet_business_desc1: "Travel at the highest level of comfort, elegance, and refinement. Our selection of Business Class vehicles is dedicated to clients who appreciate discretion, generous space, performance, and the latest generation technology.",
    fleet_business_desc2: "Whether it's for a business trip, an important event, or a special transfer, we offer you a premium experience, adapted to the most demanding standards.",
    fleet_limo_desc1: "Our limousines offer the perfect balance between elegance, comfort, and performance. Ideal for individual transfers, business meetings, or special events, these vehicles impress with their design, refinement, and a relaxing travel experience.",
    fleet_limo_desc2: "Every detail is carefully crafted to offer you safety, discretion, and comfort, regardless of the destination.",
    fleet_sprinter_desc_long: "Perfect for group travel up to 8 passengers. Our Mercedes Sprinter offers spacious comfort with premium interior, ideal for airport transfers, corporate events, and group tours.",
    fleet_passengers: "Passengers",
    fleet_leather: "Leather",
    fleet_free: "Free",
    fleet_water: "Water",
    fleet_included: "Included",
    fleet_climate: "Climate",
    fleet_individual: "Individual",
    fleet_upto: "Up to",
    
    // Service Types
    standard_premium: "Premium Limousine",
    standard_premium_desc: "Mercedes E-Class, Audi A6",
    business: "Business Class",
    business_desc: "Mercedes V-Class, Mercedes S-Class, BMW 7 Series, Audi A8",
    
    // Calculator
    calc_title: "Price Calculator",
    calc_subtitle: "Get an instant quote for your transfer",
    calc_service_type: "Service Type",
    calc_booking_type: "Booking Type",
    calc_route: "Select Route",
    calc_custom_distance: "Custom Distance (km)",
    calc_hours: "Number of Hours",
    calc_hours_min: "Minimum 4 hours",
    calc_passengers: "Number of Passengers",
    calc_calculate: "Calculate Price",
    calc_your_price: "Your Price",
    calc_book_now: "Book This Transfer",
    calc_airport_transfer: "Transfer",
    calc_airport_route: "Airport Transfer",
    calc_distance_transfer: "Distance Transfer",
    calc_hourly_booking: "Hourly Booking",
    calc_included: "Included",
    calc_waiting: "waiting time",
    calc_km_included: "km included",
    calc_perks: "Water & WiFi included",
    calc_hours_min_tooltip: "Minimum booking: 4 hours",
    calc_calculating: "Calculating distance",
    
    // Booking Form
    booking_title: "Book Your Transfer",
    booking_pickup_date: "Pickup Date",
    booking_pickup_time: "Pickup Time",
    booking_pickup_location: "Pickup Location",
    booking_dropoff_location: "Drop-off Location",
    booking_flight_number: "Flight Number (optional)",
    booking_special_requests: "Special Requests",
    booking_your_details: "Your Details",
    booking_name: "Full Name",
    booking_email: "Email Address",
    booking_phone: "Phone Number",
    booking_submit: "Confirm Booking",
    booking_success: "Booking Confirmed!",
    booking_success_msg: "Your booking has been confirmed. We will call you before we arrive.",
    
    // Availability Check (Sprinter)
    availability_title: "Check Sprinter Availability",
    availability_subtitle: "For groups up to 8 passengers, please submit an availability request",
    availability_message: "Additional Information",
    availability_submit: "Request Availability",
    availability_success: "Request Submitted!",
    availability_success_msg: "We will check availability and contact you within 24 hours.",
    
    // Contact
    contact_title: "Contact Us",
    contact_subtitle: "We're here to help with your travel needs",
    contact_address: "Address",
    contact_phone: "Phone",
    contact_email: "Email",
    contact_form_title: "Send us a Message",
    contact_subject: "Subject",
    contact_message: "Message",
    contact_send: "Send Message",
    contact_success: "Message Sent!",
    
    // Footer
    footer_company: "Exclusive Tour Transfer",
    footer_tagline: "Premium Chauffeur Service",
    footer_quick_links: "Quick Links",
    footer_legal: "Legal",
    footer_privacy: "Privacy Policy",
    footer_terms: "Terms of Service",
    footer_copyright: "© 2024 Exclusive Tour Transfer. All rights reserved.",
    footer_registration: "CHE-468.004.384",
    
    // Admin
    admin_login: "Admin Login",
    admin_email: "Email",
    admin_password: "Password",
    admin_signin: "Sign In",
    admin_dashboard: "Dashboard",
    admin_bookings: "Bookings",
    admin_requests: "Availability Requests",
    admin_messages: "Messages",
    admin_logout: "Logout",
    admin_total_bookings: "Total Bookings",
    admin_pending: "Pending",
    admin_confirmed: "Confirmed",
    admin_revenue: "Total Revenue",
    admin_status: "Status",
    admin_actions: "Actions",
    admin_confirm: "Confirm",
    admin_complete: "Complete",
    admin_cancel: "Cancel",
    admin_respond: "Respond",
    admin_decline: "Decline",
    admin_mark_read: "Mark as Read",
    
    // Route
    route_from: "From (Pickup)",
    route_to: "To (Destination)",
    route_from_placeholder: "e.g. Zürich Airport, Hotel Baur au Lac...",
    route_to_placeholder: "e.g. Basel, Geneva, Munich...",
    
    // Trip Type
    trip_type: "Trip Type",
    trip_oneway: "One Way",
    trip_roundtrip: "Round Trip",
    trip_return_date: "Return Date",
    trip_return_time: "Return Time",
    
    // Luggage
    luggage_title: "Luggage",
    luggage_small: "Small Bags",
    luggage_large: "Large Suitcases",
    luggage_small_desc: "Carry-on, backpack",
    luggage_large_desc: "Check-in suitcase",
    
    // Additional
    additional_notes: "Additional Notes",
    additional_notes_placeholder: "Any special requirements, child seats, wheelchair access, would you like a stop/break along the way, etc.",
    
    // Payment
    payment_method: "Payment Method",
    payment_card: "Pay by Card",
    payment_pickup: "Pay at Pickup",
    payment_pay_card: "Complete payment via card above",
    payment_secure: "Secure payment powered by SumUp",
    payment_error: "Payment failed. Please try again.",
    payment_success: "Payment successful!",
    loading_card_form: "Loading secure payment form...",
    retry: "Retry",
    amount_to_pay: "Amount to Pay",
    card_fill_route: "Please fill in the route details above to see the payment form.",
    proceed_to_payment: "Proceed to Card Payment",
    confirm_booking_paid: "Confirm Booking",
    payment_failed_title: "Payment Failed",
    payment_failed_msg: "The payment could not be processed. Please check your card details and try again.",
    retry_payment: "Try Again",
    confirm_and_pay: "Confirm & Pay",
    payment_processing_title: "Processing Payment...",
    payment_processing_msg: "Please wait while we process your payment. Do not close this page.",
    payment_info: "Card & Cash accepted",
    
    // Reviews
    reviews_label: "Testimonials",
    reviews_title: "What Our Clients Say",
    reviews_subtitle: "Real experiences from our valued customers",
    reviews_leave: "Leave a Review",
    reviews_thanks: "Thank you for your review!",
    reviews_name_placeholder: "Your name",
    reviews_text_placeholder: "Share your experience...",
    reviews_submit: "Submit",

    // Hourly Zurich Restriction
    hourly_zurich_only_title: "Hourly Booking - Zurich Area Only",
    hourly_zurich_only_msg: "Hourly booking is available only in the Zurich area. For special routes, please contact us:",
    hourly_zurich_only_call: "Or call us at",
    hourly_location_label: "Pickup Area",
    hourly_location_placeholder: "e.g. Zurich, Kloten, Winterthur...",
    hourly_destination_label: "Destination Area",
    hourly_destination_placeholder: "e.g. Zurich, Kloten, Winterthur...",

    // Service Cards
    service_card_cruise_title: "RIVER CRUISE TRANSFERS",
    service_card_cruise_desc: "Skip the hassle of trains and taxis. Enjoy a seamless journey from Zurich Airport directly to your river cruise ship near Basel.",
    service_card_cruise_b1: "Daily transfers from Zurich",
    service_card_cruise_b2: "5-7 passengers \u2013 premium vehicles",
    service_card_cruise_b3: "Direct transfers to your ship or hotel",
    service_card_alps_title: "Travel to the Alps \u2014 any time of the year.",
    service_card_alps_desc: "Enjoy a private, door-to-door journey from Zurich to the most popular ski resorts and destinations in Switzerland and Austria \u2013 available daily, in winter and throughout the entire year.",
    service_card_alps_b1: "Daily transfers, all year round",
    service_card_alps_b2: "Premium vehicles (up to 7 passengers)",
    service_card_alps_b3: "Professional, English-speaking drivers",

    // Common
    loading: "Loading...",
    error: "An error occurred",
    success: "Success",
    close: "Close",
    back: "Back",
    next: "Next",
    select: "Select",
    required: "Required",
  },
  de: {
    // Navigation
    nav_home: "Startseite",
    nav_services: "Dienstleistungen",
    nav_fleet: "Flotte",
    nav_booking: "Jetzt Buchen",
    nav_contact: "Kontakt",
    nav_admin: "Admin",
    
    // Hero
    hero_title: "Exclusive Tour Transfer",
    hero_subtitle: "Premium Chauffeurservice in der Schweiz",
    hero_description: "Erleben Sie Luxusreisen mit unserem professionellen Chauffeurservice. Von Flughafentransfers bis zu Stadtrundfahrten - wir liefern Exzellenz.",
    hero_cta: "Transfer Buchen",
    hero_calculate: "Preis Berechnen",
    
    // Services
    services_title: "Unsere Dienstleistungen",
    services_subtitle: "Premium-Transferlösungen nach Ihren Bedürfnissen",
    service_airport: "Flughafentransfers",
    service_airport_desc: "Komfortable und pünktliche Flughafen-Abhol- und Bringdienste mit Flugüberwachung.",
    service_distance: "Langstrecke",
    service_distance_desc: "Reisen Sie zwischen Städten in der Schweiz und Deutschland mit unseren Premium-Fahrzeugen.",
    service_hourly: "Stundenweise Buchung",
    service_hourly_desc: "Flexibler Chauffeurservice auf Stundenbasis für Geschäftstreffen oder Sightseeing-Touren.",
    
    // Fleet
    fleet_title: "Unsere Flotte",
    fleet_subtitle: "Premium-Fahrzeuge für jeden Anlass",
    fleet_limousine: "Premium Limousine",
    fleet_limousine_desc: "Bis zu 3 Passagiere",
    fleet_vclass: "Business Class",
    fleet_vclass_desc: "5-7 Passagiere",
    fleet_sprinter: "Sprinter",
    fleet_sprinter_desc: "Bis zu 8 Passagiere",
    fleet_sprinter_note: "Verfügbarkeit prüfen",
    fleet_business_desc1: "Reisen Sie auf höchstem Niveau von Komfort, Eleganz und Raffinesse. Unsere Auswahl an Business Class Fahrzeugen ist Kunden gewidmet, die Diskretion, großzügigen Raum, Leistung und neueste Technologie schätzen.",
    fleet_business_desc2: "Ob Geschäftsreise, wichtiges Event oder spezieller Transfer – wir bieten Ihnen ein Premium-Erlebnis, angepasst an die anspruchsvollsten Standards.",
    fleet_limo_desc1: "Unsere Limousinen bieten die perfekte Balance zwischen Eleganz, Komfort und Leistung. Ideal für individuelle Transfers, Geschäftstreffen oder besondere Anlässe beeindrucken diese Fahrzeuge durch Design, Raffinesse und ein entspanntes Reiseerlebnis.",
    fleet_limo_desc2: "Jedes Detail ist sorgfältig durchdacht, um Ihnen Sicherheit, Diskretion und Komfort zu bieten, unabhängig vom Ziel.",
    fleet_sprinter_desc_long: "Perfekt für Gruppenreisen bis zu 8 Passagiere. Unser Mercedes Sprinter bietet geräumigen Komfort mit Premium-Interieur, ideal für Flughafentransfers, Firmenveranstaltungen und Gruppentouren.",
    fleet_passengers: "Passagiere",
    fleet_leather: "Leder",
    fleet_free: "Gratis",
    fleet_water: "Wasser",
    fleet_included: "Inklusive",
    fleet_climate: "Klima",
    fleet_individual: "Individuell",
    fleet_upto: "Bis zu",
    
    // Service Types
    standard_premium: "Premium Limousine",
    standard_premium_desc: "Mercedes E-Klasse, Audi A6",
    business: "Business Class",
    business_desc: "Mercedes V-Klasse, Mercedes S-Klasse, BMW 7er, Audi A8",
    
    // Calculator
    calc_title: "Preisrechner",
    calc_subtitle: "Erhalten Sie sofort ein Angebot für Ihren Transfer",
    calc_service_type: "Serviceart",
    calc_booking_type: "Buchungsart",
    calc_route: "Route auswählen",
    calc_custom_distance: "Benutzerdefinierte Entfernung (km)",
    calc_hours: "Anzahl Stunden",
    calc_hours_min: "Minimum 4 Stunden",
    calc_passengers: "Anzahl Passagiere",
    calc_calculate: "Preis Berechnen",
    calc_your_price: "Ihr Preis",
    calc_book_now: "Diesen Transfer buchen",
    calc_airport_transfer: "Transfer",
    calc_airport_route: "Transfer",
    calc_distance_transfer: "Streckentransfer",
    calc_hourly_booking: "Stundenweise Buchung",
    calc_included: "Inklusive",
    calc_waiting: "Wartezeit",
    calc_km_included: "km inklusive",
    calc_perks: "Wasser & WLAN inklusive",
    calc_hours_min_tooltip: "Mindestbuchung: 4 Stunden",
    calc_calculating: "Entfernung berechnen",
    
    // Booking Form
    booking_title: "Transfer Buchen",
    booking_pickup_date: "Abholdatum",
    booking_pickup_time: "Abholzeit",
    booking_pickup_location: "Abholort",
    booking_dropoff_location: "Zielort",
    booking_flight_number: "Flugnummer (optional)",
    booking_special_requests: "Besondere Wünsche",
    booking_your_details: "Ihre Daten",
    booking_name: "Vollständiger Name",
    booking_email: "E-Mail-Adresse",
    booking_phone: "Telefonnummer",
    booking_submit: "Buchung Bestätigen",
    booking_success: "Buchung Bestätigt!",
    booking_success_msg: "Ihre Buchung wurde bestätigt. Wir rufen Sie an, bevor wir bei Ihnen ankommen.",
    
    // Availability Check (Sprinter)
    availability_title: "Sprinter Verfügbarkeit prüfen",
    availability_subtitle: "Für Gruppen bis 8 Passagiere, bitte senden Sie eine Verfügbarkeitsanfrage",
    availability_message: "Zusätzliche Informationen",
    availability_submit: "Verfügbarkeit Anfragen",
    availability_success: "Anfrage Gesendet!",
    availability_success_msg: "Wir werden die Verfügbarkeit prüfen und Sie innerhalb von 24 Stunden kontaktieren.",
    
    // Contact
    contact_title: "Kontakt",
    contact_subtitle: "Wir sind hier, um Ihnen bei Ihren Reisebedürfnissen zu helfen",
    contact_address: "Adresse",
    contact_phone: "Telefon",
    contact_email: "E-Mail",
    contact_form_title: "Senden Sie uns eine Nachricht",
    contact_subject: "Betreff",
    contact_message: "Nachricht",
    contact_send: "Nachricht Senden",
    contact_success: "Nachricht Gesendet!",
    
    // Footer
    footer_company: "Exclusive Tour Transfer",
    footer_tagline: "Premium Chauffeurservice",
    footer_quick_links: "Schnelllinks",
    footer_legal: "Rechtliches",
    footer_privacy: "Datenschutz",
    footer_terms: "AGB",
    footer_copyright: "© 2024 Exclusive Tour Transfer. Alle Rechte vorbehalten.",
    footer_registration: "CHE-468.004.384",
    
    // Admin
    admin_login: "Admin Login",
    admin_email: "E-Mail",
    admin_password: "Passwort",
    admin_signin: "Anmelden",
    admin_dashboard: "Dashboard",
    admin_bookings: "Buchungen",
    admin_requests: "Verfügbarkeitsanfragen",
    admin_messages: "Nachrichten",
    admin_logout: "Abmelden",
    admin_total_bookings: "Gesamtbuchungen",
    admin_pending: "Ausstehend",
    admin_confirmed: "Bestätigt",
    admin_revenue: "Gesamtumsatz",
    admin_status: "Status",
    admin_actions: "Aktionen",
    admin_confirm: "Bestätigen",
    admin_complete: "Abschließen",
    admin_cancel: "Stornieren",
    admin_respond: "Antworten",
    admin_decline: "Ablehnen",
    admin_mark_read: "Als gelesen markieren",
    
    // Route
    route_from: "Von (Abholung)",
    route_to: "Nach (Ziel)",
    route_from_placeholder: "z.B. Flughafen Zürich, Hotel Baur au Lac...",
    route_to_placeholder: "z.B. Basel, Genf, München...",
    
    // Trip Type
    trip_type: "Fahrtyp",
    trip_oneway: "Einfache Fahrt",
    trip_roundtrip: "Hin- und Rückfahrt",
    trip_return_date: "Rückfahrtdatum",
    trip_return_time: "Rückfahrtzeit",
    
    // Luggage
    luggage_title: "Gepäck",
    luggage_small: "Kleine Taschen",
    luggage_large: "Große Koffer",
    luggage_small_desc: "Handgepäck, Rucksack",
    luggage_large_desc: "Aufgabegepäck",
    
    // Additional
    additional_notes: "Zusätzliche Hinweise",
    additional_notes_placeholder: "Besondere Anforderungen, Kindersitze, Rollstuhlzugang, möchten Sie einen Zwischenstopp/eine Pause einlegen, etc.",
    
    // Payment
    payment_method: "Zahlungsmethode",
    payment_card: "Kartenzahlung",
    payment_pickup: "Zahlung bei Abholung",
    payment_pay_card: "Zahlung per Karte oben abschließen",
    payment_secure: "Sichere Zahlung über SumUp",
    payment_error: "Zahlung fehlgeschlagen. Bitte versuchen Sie es erneut.",
    payment_success: "Zahlung erfolgreich!",
    loading_card_form: "Sicheres Zahlungsformular wird geladen...",
    retry: "Erneut versuchen",
    amount_to_pay: "Zu zahlender Betrag",
    card_fill_route: "Bitte füllen Sie die Routendetails oben aus, um das Zahlungsformular zu sehen.",
    proceed_to_payment: "Weiter zur Kartenzahlung",
    confirm_booking_paid: "Buchung bestätigen",
    payment_failed_title: "Zahlung fehlgeschlagen",
    payment_failed_msg: "Die Zahlung konnte nicht verarbeitet werden. Bitte überprüfen Sie Ihre Kartendaten und versuchen Sie es erneut.",
    retry_payment: "Erneut versuchen",
    confirm_and_pay: "Bestätigen & Bezahlen",
    payment_processing_title: "Zahlung wird verarbeitet...",
    payment_processing_msg: "Bitte warten Sie, während wir Ihre Zahlung verarbeiten. Schließen Sie diese Seite nicht.",
    payment_info: "Karte & Bargeld akzeptiert",
    
    // Reviews
    reviews_label: "Bewertungen",
    reviews_title: "Was unsere Kunden sagen",
    reviews_subtitle: "Echte Erfahrungen unserer geschätzten Kunden",
    reviews_leave: "Bewertung hinterlassen",
    reviews_thanks: "Vielen Dank für Ihre Bewertung!",
    reviews_name_placeholder: "Ihr Name",
    reviews_text_placeholder: "Teilen Sie Ihre Erfahrung...",
    reviews_submit: "Absenden",

    // Hourly Zurich Restriction
    hourly_zurich_only_title: "Stundenweise Buchung - Nur Raum Zürich",
    hourly_zurich_only_msg: "Die stundenweise Buchung ist nur im Raum Zürich verfügbar. Für spezielle Routen kontaktieren Sie uns bitte:",
    hourly_zurich_only_call: "Oder rufen Sie uns an unter",
    hourly_location_label: "Abholbereich",
    hourly_location_placeholder: "z.B. Zürich, Kloten, Winterthur...",
    hourly_destination_label: "Zielbereich",
    hourly_destination_placeholder: "z.B. Zürich, Kloten, Winterthur...",

    // Service Cards
    service_card_cruise_title: "FLUSSKREUZFAHRT TRANSFERS",
    service_card_cruise_desc: "Vermeiden Sie den Aufwand von Zügen und Taxis. Genießen Sie eine nahtlose Reise vom Flughafen Zürich direkt zu Ihrem Flusskreuzfahrtschiff bei Basel.",
    service_card_cruise_b1: "Tägliche Transfers ab Zürich",
    service_card_cruise_b2: "5-7 Passagiere – Premium-Fahrzeuge",
    service_card_cruise_b3: "Direkttransfers zu Ihrem Schiff oder Hotel",
    service_card_alps_title: "Reisen in die Alpen — zu jeder Jahreszeit.",
    service_card_alps_desc: "Genießen Sie eine private Tür-zu-Tür-Reise von Zürich zu den beliebtesten Skigebieten und Reisezielen in der Schweiz und Österreich – täglich verfügbar, im Winter und das ganze Jahr über.",
    service_card_alps_b1: "Tägliche Transfers, das ganze Jahr",
    service_card_alps_b2: "Premium-Fahrzeuge (bis zu 7 Passagiere)",
    service_card_alps_b3: "Professionelle, englischsprachige Fahrer",

    // Common
    loading: "Laden...",
    error: "Ein Fehler ist aufgetreten",
    success: "Erfolg",
    close: "Schließen",
    back: "Zurück",
    next: "Weiter",
    select: "Auswählen",
    required: "Erforderlich",
  },
  it: {
    // Navigation
    nav_home: "Home",
    nav_services: "Servizi",
    nav_fleet: "Flotta",
    nav_booking: "Prenota Ora",
    nav_contact: "Contatto",
    nav_admin: "Admin",
    
    // Hero
    hero_title: "Exclusive Tour Transfer",
    hero_subtitle: "Servizio Autista Premium in Svizzera",
    hero_description: "Vivete il viaggio di lusso con il nostro servizio autista professionale. Dai trasferimenti aeroportuali ai tour in città, offriamo eccellenza.",
    hero_cta: "Prenota il Trasferimento",
    hero_calculate: "Calcola il Prezzo",
    
    // Services
    services_title: "I Nostri Servizi",
    services_subtitle: "Soluzioni di trasferimento premium su misura per le vostre esigenze",
    service_airport: "Trasferimenti Aeroportuali",
    service_airport_desc: "Servizi di prelievo e consegna aeroportuale confortevoli e puntuali con monitoraggio del volo.",
    service_distance: "Lunga Distanza",
    service_distance_desc: "Viaggiate tra le città di Svizzera e Germania con i nostri veicoli premium.",
    service_hourly: "Prenotazione Oraria",
    service_hourly_desc: "Servizio autista flessibile a ore per riunioni d'affari o tour turistici.",
    
    // Fleet
    fleet_title: "La Nostra Flotta",
    fleet_subtitle: "Veicoli premium per ogni occasione",
    fleet_limousine: "Premium Limousine",
    fleet_limousine_desc: "Fino a 3 passeggeri",
    fleet_vclass: "Business Class",
    fleet_vclass_desc: "5-7 passeggeri",
    fleet_sprinter: "Sprinter",
    fleet_sprinter_desc: "Fino a 8 passeggeri",
    fleet_sprinter_note: "Verifica Disponibilità",
    fleet_business_desc1: "Viaggia al massimo livello di comfort, eleganza e raffinatezza. La nostra selezione di veicoli Business Class è dedicata ai clienti che apprezzano la discrezione, lo spazio generoso, le prestazioni e la tecnologia di ultima generazione.",
    fleet_business_desc2: "Che si tratti di un viaggio d'affari, un evento importante o un trasferimento speciale, ti offriamo un'esperienza premium, adattata agli standard più esigenti.",
    fleet_limo_desc1: "Le nostre limousine offrono il perfetto equilibrio tra eleganza, comfort e prestazioni. Ideali per trasferimenti individuali, incontri d'affari o eventi speciali, questi veicoli impressionano per design, raffinatezza e un'esperienza di viaggio rilassante.",
    fleet_limo_desc2: "Ogni dettaglio è curato con attenzione per offrirti sicurezza, discrezione e comfort, indipendentemente dalla destinazione.",
    fleet_sprinter_desc_long: "Perfetto per viaggi di gruppo fino a 8 passeggeri. Il nostro Mercedes Sprinter offre comfort spazioso con interni premium, ideale per trasferimenti aeroportuali, eventi aziendali e tour di gruppo.",
    fleet_passengers: "Passeggeri",
    fleet_leather: "Pelle",
    fleet_free: "Gratuito",
    fleet_water: "Acqua",
    fleet_included: "Inclusa",
    fleet_climate: "Clima",
    fleet_individual: "Individuale",
    fleet_upto: "Fino a",
    
    // Service Types
    standard_premium: "Premium Limousine",
    standard_premium_desc: "Mercedes E-Class, Audi A6",
    business: "Business Class",
    business_desc: "Mercedes V-Class, Mercedes S-Class, BMW Serie 7, Audi A8",
    
    // Calculator
    calc_title: "Calcolatore Prezzi",
    calc_subtitle: "Ottieni un preventivo istantaneo per il tuo trasferimento",
    calc_service_type: "Tipo di Servizio",
    calc_booking_type: "Tipo di Prenotazione",
    calc_route: "Seleziona Percorso",
    calc_custom_distance: "Distanza Personalizzata (km)",
    calc_hours: "Numero di Ore",
    calc_hours_min: "Minimo 4 ore",
    calc_passengers: "Numero di Passeggeri",
    calc_calculate: "Calcola il Prezzo",
    calc_your_price: "Il Tuo Prezzo",
    calc_book_now: "Prenota Questo Trasferimento",
    calc_airport_transfer: "Trasferimento",
    calc_airport_route: "Trasferimento Aeroportuale",
    calc_distance_transfer: "Trasferimento a Distanza",
    calc_hourly_booking: "Trasferimento Orario",
    calc_included: "Incluso",
    calc_waiting: "tempo di attesa",
    calc_km_included: "km inclusi",
    calc_perks: "Acqua & WiFi inclusi",
    calc_hours_min_tooltip: "Prenotazione minima: 4 ore",
    calc_calculating: "Calcolo della distanza",
    
    // Booking Form
    booking_title: "Prenota il Trasferimento",
    booking_pickup_date: "Data di Prelievo",
    booking_pickup_time: "Ora di Prelievo",
    booking_pickup_location: "Luogo di Prelievo",
    booking_dropoff_location: "Luogo di Destinazione",
    booking_flight_number: "Numero Volo (opzionale)",
    booking_special_requests: "Richieste Speciali",
    booking_your_details: "I Tuoi Dati",
    booking_name: "Nome Completo",
    booking_email: "Indirizzo Email",
    booking_phone: "Numero di Telefono",
    booking_submit: "Conferma Prenotazione",
    booking_success: "La tua prenotazione è stata confermata!",
    booking_success_msg: "Vi chiameremo prima di arrivare da voi.",
    
    // Availability Check (Sprinter)
    availability_title: "Verifica Disponibilità Sprinter",
    availability_subtitle: "Per gruppi fino a 8 passeggeri, inviate una richiesta di disponibilità",
    availability_message: "Informazioni Aggiuntive",
    availability_submit: "Richiedi Disponibilità",
    availability_success: "Richiesta Inviata!",
    availability_success_msg: "Verificheremo la disponibilità e vi contatteremo entro 24 ore.",
    
    // Contact
    contact_title: "Contattaci",
    contact_subtitle: "Siamo qui per aiutarvi con le vostre esigenze di viaggio",
    contact_address: "Indirizzo",
    contact_phone: "Telefono",
    contact_email: "Email",
    contact_form_title: "Inviaci un Messaggio",
    contact_subject: "Oggetto",
    contact_message: "Messaggio",
    contact_send: "Invia Messaggio",
    contact_success: "Messaggio Inviato!",
    
    // Footer
    footer_company: "Exclusive Tour Transfer",
    footer_tagline: "Servizio Autista Premium",
    footer_quick_links: "Link Rapidi",
    footer_legal: "Legale",
    footer_privacy: "Informativa sulla Privacy",
    footer_terms: "Termini e Condizioni",
    footer_copyright: "© 2024 Exclusive Tour Transfer. Tutti i diritti riservati.",
    footer_registration: "CHE-468.004.384",
    
    // Admin
    admin_login: "Login Amministratore",
    admin_email: "Email",
    admin_password: "Password",
    admin_signin: "Accedi",
    admin_dashboard: "Pannello di Controllo",
    admin_bookings: "Prenotazioni",
    admin_requests: "Richieste di Disponibilità",
    admin_messages: "Messaggi",
    admin_logout: "Esci",
    admin_total_bookings: "Prenotazioni Totali",
    admin_pending: "In Attesa",
    admin_confirmed: "Confermate",
    admin_revenue: "Ricavo Totale",
    admin_status: "Stato",
    admin_actions: "Azioni",
    admin_confirm: "Conferma",
    admin_complete: "Completa",
    admin_cancel: "Annulla",
    admin_respond: "Rispondi",
    admin_decline: "Rifiuta",
    admin_mark_read: "Segna come Letto",
    
    // Route
    route_from: "Da (Prelievo)",
    route_to: "A (Destinazione)",
    route_from_placeholder: "es. Aeroporto di Zurigo, Hotel Baur au Lac...",
    route_to_placeholder: "es. Basilea, Ginevra, Monaco...",
    
    // Trip Type
    trip_type: "Tipo di Viaggio",
    trip_oneway: "Solo Andata",
    trip_roundtrip: "Andata e Ritorno",
    trip_return_date: "Data di Ritorno",
    trip_return_time: "Ora di Ritorno",
    
    // Luggage
    luggage_title: "Bagagli",
    luggage_small: "Borse Piccole",
    luggage_large: "Valigie Grandi",
    luggage_small_desc: "Bagaglio a mano, zaino",
    luggage_large_desc: "Valigia da stiva",
    
    // Additional
    additional_notes: "Note Aggiuntive",
    additional_notes_placeholder: "Requisiti speciali, seggiolini per bambini, accesso per sedia a rotelle, desiderate una sosta/pausa lungo il percorso, ecc.",
    
    // Payment
    payment_method: "Metodo di Pagamento",
    payment_card: "Pagamento con Carta",
    payment_pickup: "Pagamento al Prelievo",
    payment_pay_card: "Completa il pagamento con carta sopra",
    payment_secure: "Pagamento sicuro con SumUp",
    payment_error: "Pagamento fallito. Riprova.",
    payment_success: "Pagamento riuscito!",
    loading_card_form: "Caricamento modulo di pagamento sicuro...",
    retry: "Riprova",
    amount_to_pay: "Importo da Pagare",
    card_fill_route: "Compila i dettagli del percorso sopra per visualizzare il modulo di pagamento.",
    proceed_to_payment: "Procedi al Pagamento con Carta",
    confirm_booking_paid: "Conferma Prenotazione",
    payment_failed_title: "Pagamento Fallito",
    payment_failed_msg: "Il pagamento non è stato elaborato. Controlla i dati della carta e riprova.",
    retry_payment: "Riprova",
    confirm_and_pay: "Conferma & Paga",
    payment_processing_title: "Elaborazione pagamento...",
    payment_processing_msg: "Attendi mentre elaboriamo il pagamento. Non chiudere questa pagina.",
    payment_info: "Carta & Contanti accettati",
    
    // Reviews
    reviews_label: "Testimonianze",
    reviews_title: "Cosa dicono i nostri clienti",
    reviews_subtitle: "Esperienze reali dei nostri stimati clienti",
    reviews_leave: "Lascia una recensione",
    reviews_thanks: "Grazie per la tua recensione!",
    reviews_name_placeholder: "Il tuo nome",
    reviews_text_placeholder: "Condividi la tua esperienza...",
    reviews_submit: "Invia",

    // Hourly Zurich Restriction
    hourly_zurich_only_title: "Prenotazione Oraria - Solo Zona Zurigo",
    hourly_zurich_only_msg: "La prenotazione oraria è disponibile solo nella zona di Zurigo. Per percorsi speciali, contattaci:",
    hourly_zurich_only_call: "Oppure chiamaci al",
    hourly_location_label: "Area di Prelievo",
    hourly_location_placeholder: "es. Zurigo, Kloten, Winterthur...",
    hourly_destination_label: "Area di Destinazione",
    hourly_destination_placeholder: "es. Zurigo, Kloten, Winterthur...",

    // Service Cards
    service_card_cruise_title: "TRASFERIMENTI CROCIERE FLUVIALI",
    service_card_cruise_desc: "Evita il disagio di treni e taxi. Goditi un viaggio senza interruzioni dall'aeroporto di Zurigo direttamente alla tua nave da crociera fluviale vicino a Basilea.",
    service_card_cruise_b1: "Trasferimenti giornalieri da Zurigo",
    service_card_cruise_b2: "5-7 passeggeri – veicoli premium",
    service_card_cruise_b3: "Trasferimenti diretti alla nave o all'hotel",
    service_card_alps_title: "Viaggia verso le Alpi — in qualsiasi periodo dell'anno.",
    service_card_alps_desc: "Goditi un viaggio privato porta a porta da Zurigo verso le più popolari località sciistiche e destinazioni in Svizzera e Austria – disponibile tutti i giorni, in inverno e durante tutto l'anno.",
    service_card_alps_b1: "Trasferimenti giornalieri, tutto l'anno",
    service_card_alps_b2: "Veicoli premium (fino a 7 passeggeri)",
    service_card_alps_b3: "Autisti professionali di lingua inglese",

    // Common
    loading: "Caricamento...",
    error: "Si è verificato un errore",
    success: "Successo",
    close: "Chiudi",
    back: "Indietro",
    next: "Avanti",
    select: "Seleziona",
    required: "Obbligatorio",
  }
};

const LanguageContext = createContext();

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState('en');
  
  const t = useCallback((key) => {
    return translations[language]?.[key] || translations['en']?.[key] || key;
  }, [language]);
  
  const changeLanguage = useCallback((lang) => {
    if (translations[lang]) {
      setLanguage(lang);
      localStorage.setItem('preferred_language', lang);
    }
  }, []);
  
  // Load saved language preference
  useState(() => {
    const saved = localStorage.getItem('preferred_language');
    if (saved && translations[saved]) {
      setLanguage(saved);
    }
  });
  
  return (
    <LanguageContext.Provider value={{ language, t, changeLanguage, availableLanguages: ['en', 'de', 'it'] }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export default LanguageContext;
